import { useState, useEffect, useMemo } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { getConnectorConfig, saveConnectorConfigs } from '../utils/connectorConfig';
import {
  Shield,
  Settings,
  Save,
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  Clock,
  Plus,
  Trash2,
  X,
  Eye,
  EyeOff,
  Calendar,
  Users,
  Search,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import axios from 'axios';
import useStore from '../store/useStore';
import { formatDateTime } from '../utils/dateFormat';

const PAGE_SIZE = 10;

export default function Policies() {
  const { addNotification } = useStore();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [providerUrl, setProviderUrl] = useState(
    getConnectorConfig('policiesProviderUrl')
  );
  const [apiKey, setApiKey] = useState(
    getConnectorConfig('policiesApiKey')
  );
  const [showConfig, setShowConfig] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [apiStatus, setApiStatus] = useState('checking');
  const [showApiKey, setShowApiKey] = useState(false);
  const [newPolicy, setNewPolicy] = useState({
    id: '',
    action: 'USE',
    constraint: ''
  });
  const [policyRestrictions, setPolicyRestrictions] = useState([]);
  const [showAddRestriction, setShowAddRestriction] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const DATE_PRESETS = [
    { label: '1 week',   days: 7 },
    { label: '1 month',  days: 30 },
    { label: '3 months', days: 90 },
    { label: '1 year',   days: 365 },
  ];

  const addDays = (days) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return d;
  };

  const applyPreset = (restrictionId, field, days) => {
    updateRestriction(restrictionId, field, addDays(days));
  };

  // Add a new policy restriction
  const addRestriction = (type) => {
    const newRestriction = {
      id: Date.now(),
      type,
      ...(type === 'participantId' && { participantId: '' }),
      ...(type === 'timeRestriction' && { dateTime: '' }),
      ...(type === 'timespanRestriction' && { fromDate: '', toDate: '' }),
    };
    setPolicyRestrictions([...policyRestrictions, newRestriction]);
    setShowAddRestriction(false);
  };

  // Remove a policy restriction
  const removeRestriction = (id) => {
    setPolicyRestrictions(policyRestrictions.filter(r => r.id !== id));
  };

  // Update a restriction field
  const updateRestriction = (id, field, value) => {
    setPolicyRestrictions(policyRestrictions.map(r =>
      r.id === id ? { ...r, [field]: value } : r
    ));
  };

  useEffect(() => {
    if (providerUrl) {
      fetchPolicies();
    } else {
      setLoading(false);
    }
  }, [providerUrl]);

  useEffect(() => { setCurrentPage(1); }, [searchQuery]);

  const filteredPolicies = useMemo(() => {
    if (!searchQuery.trim()) return policies;
    const q = searchQuery.toLowerCase();
    return policies.filter(policy => {
      const id = (policy['@id'] || '').toLowerCase();
      const permissions = policy.policy?.permission || policy['edc:policy']?.['odrl:permission'];
      const permList = Array.isArray(permissions) ? permissions : permissions ? [permissions] : [];
      const constraintText = permList.map(perm => {
        const c = perm?.constraint || perm?.['odrl:constraint'];
        if (!c) return '';
        const lo = c['odrl:leftOperand'] || c.leftOperand || '';
        const ro = typeof (c['odrl:rightOperand'] || c.rightOperand) === 'object'
          ? (c['odrl:rightOperand'] || c.rightOperand)?.['@value'] || ''
          : (c['odrl:rightOperand'] || c.rightOperand || '');
        return `${lo} ${ro}`;
      }).join(' ').toLowerCase();
      return id.includes(q) || constraintText.includes(q);
    });
  }, [policies, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredPolicies.length / PAGE_SIZE));
  const pagedPolicies = filteredPolicies.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const fetchPolicies = async () => {
    if (!providerUrl) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setApiStatus('checking');

      // Check if the providerUrl is external (non-HTTPS or different domain)
      const isExternalConnector = providerUrl.startsWith('http://') ||
                                   !providerUrl.startsWith('https://enact-dataspace.iti.gr');

      let response;

      if (isExternalConnector) {
        // Use proxy for external/HTTP connectors to avoid CORS and Mixed Content issues
        const proxyPayload = {
          targetUrl: providerUrl,
          path: '/api/management/v2/policydefinitions/request',
          method: 'POST',
          data: {
            '@context': {},
            'limit': 1000,
            'offset': 0
          },
          headers: apiKey ? { 'X-Api-Key': apiKey } : {}
        };

        const headers = {
          'Content-Type': 'application/json'
        };
        const token = localStorage.getItem('kc_token');
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        response = await axios.post(
          '/api/v1/edc-proxy',
          proxyPayload,
          { headers }
        );
      } else {
        // Direct request for local HTTPS connectors
        const headers = {
          'Content-Type': 'application/json',
          ...(apiKey ? { 'X-Api-Key': apiKey } : {})
        };

        const requestBody = {
          '@context': {},
          'limit': 1000,
          'offset': 0
        };

        response = await axios.post(
          `${providerUrl}/api/management/v2/policydefinitions/request`,
          requestBody,
          { headers }
        );
      }

      const policiesData = Array.isArray(response.data) ? response.data : [];
      setPolicies(policiesData);
      setApiStatus('connected');
    } catch (error) {
      console.error('Failed to fetch policies:', error);
      setApiStatus('error');
      setPolicies([]);
    } finally {
      setLoading(false);
    }
  };

  const saveConfiguration = () => {
    saveConnectorConfigs({ policiesProviderUrl: providerUrl, policiesApiKey: apiKey });
    setShowConfig(false);
    fetchPolicies();
  };

  const refreshData = () => {
    fetchPolicies();
  };

  const createPolicy = async () => {
    if (!newPolicy.id) {
      addNotification({ type: 'error', message: 'Please provide a Policy ID' });
      return;
    }

    try {
      // Build ODRL constraints from restrictions
      const odrlConstraints = policyRestrictions.map(restriction => {
        if (restriction.type === 'participantId' && restriction.participantId) {
          return {
            'odrl:leftOperand': 'REFERRING_CONNECTOR',
            'odrl:operator': { '@id': 'odrl:eq' },
            'odrl:rightOperand': restriction.participantId
          };
        }
        if (restriction.type === 'timeRestriction' && restriction.dateTime) {
          return {
            'odrl:leftOperand': 'edc:dateTime',
            'odrl:operator': { '@id': 'odrl:lteq' },
            'odrl:rightOperand': { '@value': new Date(restriction.dateTime).toISOString(), '@type': 'xsd:dateTime' }
          };
        }
        if (restriction.type === 'timespanRestriction' && restriction.fromDate && restriction.toDate) {
          return [
            {
              'odrl:leftOperand': 'edc:dateTime',
              'odrl:operator': { '@id': 'odrl:gteq' },
              'odrl:rightOperand': { '@value': new Date(restriction.fromDate).toISOString(), '@type': 'xsd:dateTime' }
            },
            {
              'odrl:leftOperand': 'edc:dateTime',
              'odrl:operator': { '@id': 'odrl:lteq' },
              'odrl:rightOperand': { '@value': new Date(restriction.toDate).toISOString(), '@type': 'xsd:dateTime' }
            }
          ];
        }
        return null;
      }).filter(Boolean).flat();

      // Add PURPOSE constraint if specified
      if (newPolicy.constraint) {
        odrlConstraints.push({
          'odrl:leftOperand': 'PURPOSE',
          'odrl:operator': { '@id': 'odrl:eq' },
          'odrl:rightOperand': newPolicy.constraint
        });
      }

      const policyPayload = {
        '@context': {
          '@vocab': 'https://w3id.org/edc/v0.0.1/ns/',
          'odrl': 'http://www.w3.org/ns/odrl/2/',
          'xsd': 'http://www.w3.org/2001/XMLSchema#',
          'edc': 'https://w3id.org/edc/v0.0.1/ns/'
        },
        '@id': newPolicy.id,
        'policy': {
          '@type': 'odrl:Set',
          'odrl:permission': [
            {
              'odrl:action': newPolicy.action,
              ...(odrlConstraints.length > 0 && {
                'odrl:constraint': odrlConstraints.length === 1 ? odrlConstraints[0] : {
                  '@type': 'odrl:LogicalConstraint',
                  'odrl:and': odrlConstraints
                }
              })
            }
          ],
          'odrl:prohibition': [],
          'odrl:obligation': []
        }
      };

      const isExternalConnector = providerUrl.startsWith('http://') ||
                                   !providerUrl.startsWith('https://enact-dataspace.iti.gr');

      if (isExternalConnector) {
        // Use proxy for external connectors
        const headers = {
          'Content-Type': 'application/json'
        };
        const token = localStorage.getItem('kc_token');
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        await axios.post(
          '/api/v1/edc-proxy',
          {
            targetUrl: providerUrl,
            path: '/api/management/v2/policydefinitions',
            method: 'POST',
            data: policyPayload,
            headers: apiKey ? { 'X-Api-Key': apiKey } : {}
          },
          { headers }
        );
      } else {
        // Direct request for local HTTPS connectors
        await axios.post(
          `${providerUrl}/api/management/v2/policydefinitions`,
          policyPayload,
          {
            headers: {
              'Content-Type': 'application/json',
              ...(apiKey ? { 'X-Api-Key': apiKey } : {})
            }
          }
        );
      }

      addNotification({ type: 'success', message: `Policy "${newPolicy.id}" created successfully!` });
      setShowCreateModal(false);
      setNewPolicy({ id: '', action: 'USE', constraint: '' });
      setPolicyRestrictions([]);
      fetchPolicies();
    } catch (error) {
      console.error('Failed to create policy:', error);
      addNotification({ type: 'error', message: `Failed to create policy: ${error.response?.data?.message || error.message}` });
    }
  };

  const deletePolicy = async (policyId) => {
    if (!confirm(`Are you sure you want to delete policy "${policyId}"?`)) {
      return;
    }

    try {
      const isExternalConnector = providerUrl.startsWith('http://') ||
                                   !providerUrl.startsWith('https://enact-dataspace.iti.gr');

      if (isExternalConnector) {
        // Use proxy for external connectors
        const headers = {
          'Content-Type': 'application/json'
        };
        const token = localStorage.getItem('kc_token');
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        await axios.post(
          '/api/v1/edc-proxy',
          {
            targetUrl: providerUrl,
            path: `/api/management/v2/policydefinitions/${encodeURIComponent(policyId)}`,
            method: 'DELETE',
            headers: apiKey ? { 'X-Api-Key': apiKey } : {}
          },
          { headers }
        );
      } else {
        // Direct request for local HTTPS connectors
        await axios.delete(
          `${providerUrl}/api/management/v2/policydefinitions/${encodeURIComponent(policyId)}`,
          {
            headers: {
              'Content-Type': 'application/json',
              ...(apiKey ? { 'X-Api-Key': apiKey } : {})
            }
          }
        );
      }

      addNotification({ type: 'success', message: `Policy "${policyId}" deleted.` });
      fetchPolicies();
    } catch (error) {
      console.error('Failed to delete policy:', error);
      let errorMessage = 'Failed to delete policy';
      if (error.response?.status === 409) {
        errorMessage = `Cannot delete policy "${policyId}": it is in use by a contract definition.`;
      } else if (error.response?.data?.message) {
        errorMessage = `Failed to delete policy: ${error.response.data.message}`;
      } else {
        errorMessage = `Failed to delete policy: ${error.message}`;
      }
      addNotification({ type: 'error', message: errorMessage });
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected':
        return <CheckCircle className="text-green-400" size={20} />;
      case 'error':
        return <AlertTriangle className="text-red-400" size={20} />;
      default:
        return <Clock className="text-yellow-400 animate-pulse" size={20} />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'connected':
        return 'Connected';
      case 'error':
        return 'Connection Error';
      default:
        return 'Checking...';
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-display text-white mb-2">
            Access Policies
          </h1>
          <p className="text-gray-400">
            View policies from Provider EDC connector
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowConfig(!showConfig)}
            className="btn btn-secondary"
          >
            <Settings size={20} />
            Configure Connector
          </button>
          {providerUrl && (
            <>
              <button
                onClick={() => setShowCreateModal(true)}
                className="btn btn-primary"
              >
                <Plus size={20} />
                Create Policy
              </button>
              <button
                onClick={refreshData}
                className="btn btn-secondary"
                disabled={loading}
              >
                <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
                Refresh
              </button>
            </>
          )}
        </div>
      </div>

      {/* Configuration Panel */}
      {showConfig && (
        <div className="card border-blue-500/30">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Settings size={20} className="text-blue-400" />
            Provider EDC Configuration
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Management API URL *
              </label>
              <input
                type="text"
                value={providerUrl}
                onChange={(e) => setProviderUrl(e.target.value)}
                
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {/* <p className="text-xs text-gray-500 mt-1">
                Management API URL (proxied through Nginx). Examples:<br/>
                • https://enact-dataspace.iti.gr/edc-provider-api<br/>
                • http://dev.ithaca.ece.uowm.gr:11002 (direct access)
              </p> */}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                API Key (optional)
              </label>
              <div className="relative">
                <input
                  type={showApiKey ? "text" : "password"}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder=""
                  className="w-full px-4 py-2 pr-12 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300 transition-colors"
                >
                  {showApiKey ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={saveConfiguration}
                className="btn btn-primary"
              >
                <Save size={20} />
                Save & Connect
              </button>
              <button
                onClick={() => setShowConfig(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Connection Status */}
      {!providerUrl && !showConfig && (
        <div className="card border-yellow-500/30 bg-yellow-900/10">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-yellow-400 flex-shrink-0 mt-1" size={24} />
            <div>
              <h3 className="text-lg font-semibold text-yellow-400 mb-2">
                No Provider Connector Configured
              </h3>
              <p className="text-gray-300 mb-3">
                Please configure your Provider EDC connector endpoint to view policies.
              </p>
              <button
                onClick={() => setShowConfig(true)}
                className="btn btn-primary"
              >
                <Settings size={20} />
                Configure Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* API Status */}
      {providerUrl && (
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Provider EDC Connector</p>
              <p className="text-lg font-semibold mt-1">{getStatusText(apiStatus)}</p>
            </div>
            {getStatusIcon(apiStatus)}
          </div>
        </div>
      )}

      {/* Create Policy Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="card max-w-2xl w-full mx-4 border-blue-500/30">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold flex items-center gap-2">
                <Plus size={24} className="text-blue-400" />
                Create New Policy
              </h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <X size={24} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Policy ID *
                </label>
                <input
                  type="text"
                  value={newPolicy.id}
                  onChange={(e) => setNewPolicy(prev => ({ ...prev, id: e.target.value }))}
                  
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Action
                </label>
                <select
                  value={newPolicy.action}
                  onChange={(e) => setNewPolicy(prev => ({ ...prev, action: e.target.value }))}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="USE">USE</option>
                  <option value="TRANSFER">TRANSFER</option>
                  <option value="READ">READ</option>
                  <option value="WRITE">WRITE</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Purpose Constraint (optional)
                </label>
                <input
                  type="text"
                  value={newPolicy.constraint}
                  onChange={(e) => setNewPolicy(prev => ({ ...prev, constraint: e.target.value }))}
                  placeholder="e.g., research, commercial"
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Leave empty for no purpose constraint
                </p>
              </div>

              {/* Publishing Restrictions */}
              <div className="border-t border-gray-700 pt-4 mt-4">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-gray-300 flex items-center gap-2">
                    <Shield size={16} className="text-blue-400" />
                    Publishing Restrictions
                  </label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowAddRestriction(!showAddRestriction)}
                      className="text-xs px-3 py-1 bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 rounded-lg flex items-center gap-1 transition-colors"
                    >
                      <Plus size={14} />
                      Add Restriction
                    </button>
                    {showAddRestriction && (
                      <div className="absolute right-0 top-full mt-1 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-10 min-w-48">
                        <button
                          onClick={() => addRestriction('participantId')}
                          className="w-full px-4 py-2 text-left text-sm hover:bg-gray-700 flex items-center gap-2 rounded-t-lg"
                        >
                          <Users size={14} className="text-purple-400" />
                          Consumer's Participant ID
                        </button>
                        <button
                          onClick={() => addRestriction('timeRestriction')}
                          className="w-full px-4 py-2 text-left text-sm hover:bg-gray-700 flex items-center gap-2"
                        >
                          <Clock size={14} className="text-yellow-400" />
                          Time Restriction
                        </button>
                        <button
                          onClick={() => addRestriction('timespanRestriction')}
                          className="w-full px-4 py-2 text-left text-sm hover:bg-gray-700 flex items-center gap-2 rounded-b-lg"
                        >
                          <Calendar size={14} className="text-green-400" />
                          Timespan Restriction
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {policyRestrictions.length === 0 ? (
                  <p className="text-xs text-gray-500 italic">No restrictions - policy will allow all participants</p>
                ) : (
                  <div className="space-y-3 max-h-48 overflow-y-auto">
                    {policyRestrictions.map((restriction) => (
                      <div key={restriction.id} className="bg-gray-800/50 rounded-lg p-3 border border-gray-700">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium flex items-center gap-2">
                            {restriction.type === 'participantId' && (
                              <><Users size={12} className="text-purple-400" /> Consumer Participant ID</>
                            )}
                            {restriction.type === 'timeRestriction' && (
                              <><Clock size={12} className="text-yellow-400" /> Time Restriction</>
                            )}
                            {restriction.type === 'timespanRestriction' && (
                              <><Calendar size={12} className="text-green-400" /> Timespan Restriction</>
                            )}
                          </span>
                          <button
                            onClick={() => removeRestriction(restriction.id)}
                            className="text-gray-500 hover:text-red-400 transition-colors"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>

                        {restriction.type === 'participantId' && (
                          <div>
                            <input
                              type="text"
                              value={restriction.participantId || ''}
                              onChange={(e) => updateRestriction(restriction.id, 'participantId', e.target.value)}
                              
                              className="w-full px-3 py-1.5 text-sm bg-gray-900 border border-gray-600 rounded focus:ring-1 focus:ring-blue-500"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Enter the consumer's participant ID (the <code>MY_EDC_PARTICIPANT_ID</code> value set on their connector). Only this connector will be able to negotiate contracts.
                            </p>
                          </div>
                        )}

                        {restriction.type === 'timeRestriction' && (
                          <div className="space-y-2">
                            <p className="text-xs text-gray-500">Access expires at:</p>
                            <div className="flex flex-wrap gap-1">
                              {DATE_PRESETS.map(p => (
                                <button key={p.label} type="button"
                                  onClick={() => applyPreset(restriction.id, 'dateTime', p.days)}
                                  className="px-2 py-0.5 text-xs bg-gray-700 hover:bg-yellow-600/30 hover:text-yellow-300 border border-gray-600 rounded transition-colors">
                                  +{p.label}
                                </button>
                              ))}
                            </div>
                            <DatePicker
                              selected={restriction.dateTime ? new Date(restriction.dateTime) : null}
                              onChange={(date) => updateRestriction(restriction.id, 'dateTime', date)}
                              showTimeSelect
                              timeFormat="HH:mm"
                              timeIntervals={15}
                              dateFormat="dd/MM/yyyy HH:mm"
                              minDate={new Date()}
                              placeholderText="Select expiry date & time"
                              className="w-full px-3 py-1.5 text-sm bg-gray-900 border border-gray-600 rounded focus:ring-1 focus:ring-yellow-500 text-gray-200"
                              wrapperClassName="w-full"
                              popperPlacement="bottom-start"
                            />
                          </div>
                        )}

                        {restriction.type === 'timespanRestriction' && (
                          <div className="space-y-2">
                            <div className="flex flex-wrap gap-1">
                              {DATE_PRESETS.map(p => (
                                <button key={p.label} type="button"
                                  onClick={() => {
                                    updateRestriction(restriction.id, 'fromDate', new Date());
                                    applyPreset(restriction.id, 'toDate', p.days);
                                  }}
                                  className="px-2 py-0.5 text-xs bg-gray-700 hover:bg-green-600/30 hover:text-green-300 border border-gray-600 rounded transition-colors">
                                  Next {p.label}
                                </button>
                              ))}
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              <div>
                                <label className="text-xs text-gray-500 mb-1 block">From</label>
                                <DatePicker
                                  selected={restriction.fromDate ? new Date(restriction.fromDate) : null}
                                  onChange={(date) => updateRestriction(restriction.id, 'fromDate', date)}
                                  showTimeSelect
                                  timeFormat="HH:mm"
                                  timeIntervals={15}
                                  dateFormat="dd/MM/yyyy HH:mm"
                                  minDate={new Date()}
                                  placeholderText="Start date & time"
                                  className="w-full px-3 py-1.5 text-sm bg-gray-900 border border-gray-600 rounded focus:ring-1 focus:ring-green-500 text-gray-200"
                                  wrapperClassName="w-full"
                                  popperPlacement="bottom-start"
                                />
                              </div>
                              <div>
                                <label className="text-xs text-gray-500 mb-1 block">To</label>
                                <DatePicker
                                  selected={restriction.toDate ? new Date(restriction.toDate) : null}
                                  onChange={(date) => updateRestriction(restriction.id, 'toDate', date)}
                                  showTimeSelect
                                  timeFormat="HH:mm"
                                  timeIntervals={15}
                                  dateFormat="dd/MM/yyyy HH:mm"
                                  minDate={restriction.fromDate ? new Date(restriction.fromDate) : new Date()}
                                  placeholderText="End date & time"
                                  className="w-full px-3 py-1.5 text-sm bg-gray-900 border border-gray-600 rounded focus:ring-1 focus:ring-green-500 text-gray-200"
                                  wrapperClassName="w-full"
                                  popperPlacement="bottom-start"
                                />
                              </div>
                            </div>
                            {restriction.fromDate && restriction.toDate && (
                              <p className="text-xs text-green-400">
                                {formatDateTime(restriction.fromDate)} → {formatDateTime(restriction.toDate)}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={createPolicy}
                  className="btn btn-primary flex-1"
                >
                  <Save size={20} />
                  Create Policy
                </button>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setPolicyRestrictions([]);
                  }}
                  className="btn btn-secondary flex-1"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Policies List */}
      {providerUrl && policies.length > 0 && (
        <div className="card">
          <div className="flex items-center justify-between mb-4 gap-4">
            <h2 className="text-xl font-semibold flex items-center gap-2 shrink-0">
              <Shield className="text-green-400" size={24} />
              Access Policies ({filteredPolicies.length}{filteredPolicies.length !== policies.length ? ` / ${policies.length}` : ''})
            </h2>
            <div className="relative max-w-sm w-full">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search by ID or constraint…"
                className="w-full pl-9 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white">
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          {filteredPolicies.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-6">No policies match your search.</p>
          ) : (
            <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {pagedPolicies.map((policy) => {
              const permissions = policy.policy?.permission || policy['edc:policy']?.['odrl:permission'];
              const permList = Array.isArray(permissions) ? permissions : permissions ? [permissions] : [];

              // Extract constraint info
              const constraints = [];
              permList.forEach(perm => {
                const constraint = perm?.constraint || perm?.['odrl:constraint'];
                if (constraint) {
                  const leftOp = constraint['odrl:leftOperand'] || constraint.leftOperand || '';
                  const rightOp = constraint['odrl:rightOperand'] || constraint.rightOperand || '';
                  const operator = constraint['odrl:operator']?.['@id'] || constraint.operator || '';
                  if (leftOp === 'REFERRING_CONNECTOR') {
                    constraints.push({ type: 'consumer', value: rightOp, operator });
                  } else if (leftOp === 'edc:participantId') {
                    constraints.push({ type: 'participant', value: rightOp, operator });
                  } else if (leftOp === 'edc:dateTime') {
                    const dateVal = typeof rightOp === 'object' ? rightOp['@value'] : rightOp;
                    constraints.push({ type: 'time', value: dateVal, operator });
                  } else if (leftOp === 'PURPOSE') {
                    constraints.push({ type: 'purpose', value: rightOp, operator });
                  } else if (leftOp) {
                    constraints.push({ type: 'other', value: `${leftOp} ${rightOp}`, operator });
                  }
                }
                // Check for LogicalConstraint (multiple constraints with AND)
                const logicalAnd = constraint?.['odrl:and'];
                if (Array.isArray(logicalAnd)) {
                  logicalAnd.forEach(c => {
                    const lo = c['odrl:leftOperand'] || '';
                    const ro = c['odrl:rightOperand'] || '';
                    if (lo === 'edc:dateTime') {
                      const dateVal = typeof ro === 'object' ? ro['@value'] : ro;
                      constraints.push({ type: 'time', value: dateVal, operator: c['odrl:operator']?.['@id'] || '' });
                    }
                  });
                }
              });

              const action = permList[0]?.['odrl:action']?.['odrl:type'] || permList[0]?.['odrl:action'] || permList[0]?.action || 'USE';

              return (
                <div key={policy['@id']} className="p-4 bg-gray-800/50 rounded-lg border border-gray-700 hover:border-green-500/50 transition-colors group">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-white truncate flex-1">
                      {policy['@id']}
                    </h3>
                    <button
                      onClick={() => deletePolicy(policy['@id'])}
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-red-400 hover:text-red-300 ml-2"
                      title="Delete policy"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs px-2 py-0.5 bg-blue-600/20 text-blue-400 rounded-full font-medium">
                      {typeof action === 'string' ? action : 'USE'}
                    </span>
                    {constraints.length === 0 && (
                      <span className="text-xs px-2 py-0.5 bg-green-600/20 text-green-400 rounded-full">
                        Unrestricted
                      </span>
                    )}
                  </div>
                  {constraints.length > 0 && (
                    <div className="space-y-1 mt-2">
                      {constraints.map((c, i) => (
                        <div key={i} className="text-xs flex items-center gap-1.5">
                          {c.type === 'consumer' && (
                            <>
                              <Users size={12} className="text-purple-400 flex-shrink-0" />
                              <span className="text-purple-300">Restricted to:</span>
                              <span className="text-gray-300 truncate" title={c.value}>{c.value}</span>
                            </>
                          )}
                          {c.type === 'participant' && (
                            <>
                              <Users size={12} className="text-purple-400 flex-shrink-0" />
                              <span className="text-purple-300">Participant:</span>
                              <span className="text-gray-300 truncate" title={c.value}>{c.value}</span>
                            </>
                          )}
                          {c.type === 'time' && (
                            <>
                              <Clock size={12} className="text-yellow-400 flex-shrink-0" />
                              <span className="text-yellow-300">
                                {c.operator.includes('gteq') ? 'From:' : 'Until:'}
                              </span>
                              <span className="text-gray-300">
                                {formatDateTime(c.value)}
                              </span>
                            </>
                          )}
                          {c.type === 'purpose' && (
                            <>
                              <Shield size={12} className="text-blue-400 flex-shrink-0" />
                              <span className="text-blue-300">Purpose:</span>
                              <span className="text-gray-300">{c.value}</span>
                            </>
                          )}
                          {c.type === 'other' && (
                            <span className="text-gray-400">{c.value}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-700">
              <p className="text-sm text-gray-400">
                Showing {((currentPage - 1) * PAGE_SIZE) + 1}–{Math.min(currentPage * PAGE_SIZE, filteredPolicies.length)} of {filteredPolicies.length}
              </p>
              <div className="flex items-center gap-1">
                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                  className="p-2 rounded-lg hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  <ChevronLeft size={16} />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                  .reduce((acc, p, idx, arr) => {
                    if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((item, idx) =>
                    item === '…' ? (
                      <span key={`ellipsis-${idx}`} className="px-2 text-gray-500">…</span>
                    ) : (
                      <button key={item} onClick={() => setCurrentPage(item)}
                        className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${item === currentPage ? 'bg-blue-600 text-white' : 'hover:bg-gray-700 text-gray-300'}`}>
                        {item}
                      </button>
                    )
                  )}
                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                  className="p-2 rounded-lg hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
            </>
          )}
        </div>
      )}

      {/* Empty State */}
      {providerUrl && !loading && policies.length === 0 && apiStatus === 'connected' && (
        <div className="card text-center py-12">
          <Shield className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400">
            No policies found in the configured Provider EDC connector.
          </p>
        </div>
      )}
    </div>
  );
}